import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SettingsTab from "../admin/SettingsTab";
import HeroTab from "../admin/HeroTab";
import ContentTab from "../admin/ContentTab";
import CrudTab from "../admin/CrudTab";
import MessagesTab from "../admin/MessagesTab";

const TABS = [
    { id: "settings", label: "Site Settings" },
    { id: "hero", label: "Hero" },
    { id: "content", label: "Content" },
    { id: "people", label: "People" },
    { id: "talents", label: "Talents" },
    { id: "projects", label: "Projects" },
    { id: "partners", label: "Partners" },
    { id: "messages", label: "Messages" },
];

const PEOPLE_FIELDS = [
    { key: "name", label: "Name", type: "text" },
    { key: "role", label: "Role", type: "bi" },
    { key: "bio", label: "Short Biography", type: "biarea" },
    { key: "photo_url", label: "Portrait URL", type: "image" },
    { key: "linkedin", label: "LinkedIn URL", type: "text" },
    { key: "instagram", label: "Instagram URL", type: "text" },
    { key: "order", label: "Display Order", type: "number" },
    { key: "active", label: "Active (visible on site)", type: "switch", row: true },
];

const TALENT_FIELDS = [
    { key: "name", label: "Name", type: "text" },
    { key: "role", label: "Title / Role (e.g. Former Player, Coach, Portuguese International)", type: "bi" },
    { key: "bio", label: "Short Biography", type: "biarea" },
    { key: "photo_url", label: "Profile Photo", type: "image" },
    { key: "category", label: "Category (for future filters: players / former_players / coaches / speakers / ambassadors)", type: "text" },
    { key: "website", label: "'Learn More' link (optional — profile page, article, video…)", type: "text" },
    { key: "instagram", label: "Instagram URL", type: "text" },
    { key: "linkedin", label: "LinkedIn URL", type: "text" },
    { key: "order", label: "Display Order", type: "number" },
    { key: "active", label: "Active (visible on site)", type: "switch", row: true },
];

const PROJECT_FIELDS = [
    { key: "name", label: "Project Name", type: "bi" },
    { key: "client", label: "Client", type: "text" },
    { key: "category", label: "Category", type: "bi" },
    { key: "image_url", label: "Hero Image URL", type: "image" },
    { key: "video_url", label: "Optional Video URL (MP4 / YouTube / Vimeo)", type: "text" },
    { key: "description", label: "Short Description", type: "biarea" },
    { key: "year", label: "Year", type: "text" },
    { key: "url", label: "Project URL", type: "text" },
    { key: "order", label: "Display Order", type: "number" },
    { key: "featured", label: "Featured on homepage", type: "switch", row: true },
];

const PARTNER_FIELDS = [
    { key: "name", label: "Partner Name", type: "text" },
    { key: "logo_url", label: "Logo URL", type: "image" },
    { key: "description", label: "Description", type: "biarea" },
    { key: "video_url", label: "Video URL (MP4 / YouTube / Vimeo)", type: "text" },
    { key: "image_url", label: "Image URL", type: "image" },
    { key: "url", label: "External URL", type: "text" },
    { key: "order", label: "Display Order", type: "number" },
    { key: "active", label: "Active (visible on site)", type: "switch", row: true },
];

export default function Admin() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [tab, setTab] = useState("settings");

    useEffect(() => {
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => {
                localStorage.removeItem("btf-token");
                navigate("/admin/login");
            });
    }, [navigate]);

    if (!user) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F4F3EF]" data-testid="admin-loading">
                <span className="font-display animate-pulse text-5xl text-[#D60024]">+</span>
            </div>
        );
    }

    const logout = () => {
        localStorage.removeItem("btf-token");
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-[#F4F3EF] text-[#0A0A0A]" data-testid="admin-dashboard">
            <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A] text-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
                    <span className="font-display text-2xl leading-none">
                        BTF<span className="text-[#D60024]">+</span> <span className="ml-2 align-middle font-body text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">Admin</span>
                    </span>
                    <div className="flex items-center gap-5 text-[11px] font-semibold uppercase tracking-[0.2em]">
                        <a href="/" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white" data-testid="admin-view-site">View site</a>
                        <span className="hidden text-white/40 sm:inline">{user.email}</span>
                        <button onClick={logout} data-testid="admin-logout-btn" className="text-[#D60024] hover:text-white">Logout</button>
                    </div>
                </div>
                <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-6 pb-0">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`whitespace-nowrap border-b-2 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                                tab === t.id ? "border-[#D60024] text-white" : "border-transparent text-white/45 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
            </header>

            <main className="mx-auto max-w-6xl px-6 py-10">
                {tab === "settings" && <SettingsTab />}
                {tab === "hero" && <HeroTab />}
                {tab === "content" && <ContentTab />}
                {tab === "people" && (
                    <CrudTab resource="people" title="People" fields={PEOPLE_FIELDS} empty={{ name: "", role: { en: "", pt: "" }, bio: { en: "", pt: "" }, photo_url: "", linkedin: "", instagram: "", order: 99, active: true }} />
                )}
                {tab === "talents" && (
                    <CrudTab resource="talents" title="Talents" fields={TALENT_FIELDS} empty={{ name: "", role: { en: "", pt: "" }, bio: { en: "", pt: "" }, photo_url: "", category: "", website: "", instagram: "", linkedin: "", order: 99, active: true }} />
                )}
                {tab === "projects" && (
                    <CrudTab resource="projects" title="Projects" fields={PROJECT_FIELDS} empty={{ name: { en: "", pt: "" }, client: "", category: { en: "", pt: "" }, image_url: "", video_url: "", description: { en: "", pt: "" }, year: "", url: "", order: 99, featured: false }} />
                )}
                {tab === "partners" && (
                    <CrudTab resource="partners" title="Partners" fields={PARTNER_FIELDS} empty={{ name: "", logo_url: "", description: { en: "", pt: "" }, video_url: "", image_url: "", url: "", order: 99, active: true }} />
                )}
                {tab === "messages" && <MessagesTab />}
            </main>
        </div>
    );
}
